import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { connectState } from '@examdojo/angular/util';
import {
  getAnimationAppearDisappear,
  getAnimationSlideInToLeft,
  getAnimationSlideInToRight,
} from '@examdojo/animation';
import { createLSValueMaintainer } from '@examdojo/browser/local-storage';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AngularSplitModule, SplitGutterInteractionEvent } from 'angular-split';
import { BehaviorSubject } from 'rxjs';
import { AssetExplorerComponent } from '../explorer';
import { AssetTreeBuilderService } from '../explorer/asset-tree-builder';

const ASSET_TREE_WIDTH_LOCAL_STORAGE_KEY = '__y42_assetTreeWidth';

@UntilDestroy()
@Component({
  selector: 'exam-asset-tree-view',
  imports: [AngularSplitModule, AssetExplorerComponent],
  providers: [AssetTreeBuilderService],
  templateUrl: './asset-tree-view.component.html',
  styleUrls: ['./asset-tree-view.component.scss'],
  host: { class: 'bg-neutral-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    getAnimationSlideInToRight(),
    getAnimationSlideInToLeft({
      transformX: {
        initial: '0',
        transformed: '-100%',
      },
    }),
    getAnimationAppearDisappear(),
  ],
})
export class AssetTreeViewComponent {
  // as-split-area[size] accepts only number
  // although docs clearly say that "*" is also valid
  readonly sizeWildcard = '*' as unknown as number;
  readonly DRAG_TARGET = 1;

  readonly MIN_LEFT_NAV_WIDTH_PX = 230;
  readonly MAX_LEFT_NAV_WIDTH_PX = 480;
  readonly INITIAL_LEFT_NAV_WIDTH_PX = 320;

  private readonly lsAssetTreeWidthMaintainer = createLSValueMaintainer<number>(
    ASSET_TREE_WIDTH_LOCAL_STORAGE_KEY,
    'width',
  );

  private readonly assetTreeAsSplitWidth$$ = new BehaviorSubject<number>(
    this.lsAssetTreeWidthMaintainer.getValue(this.INITIAL_LEFT_NAV_WIDTH_PX),
  );

  @Input() readOnly = false;
  @Input() hasPadding = false;

  readonly state = connectState({
    assetTreeAsSplitWidth: this.assetTreeAsSplitWidth$$,
  });

  sizeChanged(event: SplitGutterInteractionEvent) {
    const assetTreeAsSplitWidth = event.sizes.at(1);
    if (typeof assetTreeAsSplitWidth === 'number') {
      this.updateAssetTreeAsSplitWidth(assetTreeAsSplitWidth);
    }
  }

  gutterClick() {
    const currentAssetTreeAsSplitWidth = this.assetTreeAsSplitWidth$$.getValue();
    if (currentAssetTreeAsSplitWidth === 0) {
      this.updateAssetTreeAsSplitWidth(this.INITIAL_LEFT_NAV_WIDTH_PX);
    } else {
      this.updateAssetTreeAsSplitWidth(0);
    }
  }

  private updateAssetTreeAsSplitWidth(width: number) {
    this.assetTreeAsSplitWidth$$.next(width);
    this.lsAssetTreeWidthMaintainer.setValue(width);
  }
}
